import React, {useEffect, useMemo} from 'react'
import {UIGameActionButtonSettlement} from './UIGameActionButtonSettlement/index.ts'
import clsx from 'clsx'
import styles from './UIGameActionButtonContainer.module.scss'
import {UIGameActionButtonRoad} from './UIGameActionButtonRoad/index.ts'
import {UIGameActionButtonCity} from './UIGameActionButtonCity/index.ts'
import {UIGameActionButtonTurn} from './UIGameActionButtonTurn/index.ts'
import {UIGameActionButtonBuyDevelopmentCard} from './UIGameActionButtonBuyDevelopmentCard/index.ts'
import {UIGameActionButtonTrade} from './UIGameActionButtonTrade/index.ts'
import {UIGameActionButtonShip} from './UIGameActionButtonShip/index.ts'
import {UIGameActionButtonShipMove} from './UIGameActionButtonShipMove/index.ts'
import {UIGameActionButtonSpecialBuildPhase} from './UIGameActionButtonSpecialBuildPhase/index.ts'
import {UIGameActionButtonCityWall} from './UIGameActionButtonCityWall/index.ts'
import {UIGameActionButtonCityImprovements} from './UIGameActionButtonCityImprovements/index.ts'
import {UIGameActionButtonKnightActions} from './UIGameActionButtonKnightActions/index.ts'
import {useGameStore} from '../../store/GameStoreContext.ts'
import {UIGameTimerContainer} from '../UIGameTimer/UIGameTimerContainer.tsx'
import {UIGameCurrentStatusContainer} from '../UIGameCurrentStatus/index.ts'
import {ModeSettingEnum} from '@colonist/types'
import {useIsReplay} from '../../store/selectors/useIsReplay.ts'
import {useCurrentStateValidator} from '../../store/selectors/useCurrentStateValidator.ts'

export interface UIGameActionButtonContainerProps {
    className?: string | undefined
    isPortraitLayout?: boolean
}

const UIGameActionButtonContainerLandscape = ({
    className,
}: UIGameActionButtonContainerProps) => {
    const isReplay = useIsReplay()
    const isShowTradePanel = useGameStore((state) => state.gameTrade.isShowTradePanel)
    const gameSettings = useGameStore((state) => state.gameSettings)
    const hasCityWallButton = useGameStore((state) => state.gameState.mechanicCityWallState != null)
    const hasShipButtons = useGameStore((state) => state.gameState.mechanicShipState != null)
    const hasSpecialBuildPhaseButton = useGameStore((state) => state.gameState.mechanicSpecialBuildPhaseState != null)
    const hasBuyDevelopmentCardButton = useGameStore((state) => state.gameState.mechanicDevelopmentCardsState != null)
    const hasCityImprovementButtons = useGameStore((state) => state.gameState.mechanicCityImprovementsState != null)
    const hasBothShipAndCityImprovementButtons = hasCityImprovementButtons && hasShipButtons
    const isMyColorSpectator = useGameStore((state) => state.isMyColorSpectator)
    const closeAllExtraButtons = useGameStore((state) => state.gameActionButtons.closeAllExtraButtons)
    const hasActionBoxOpening = useGameStore((state) => state.actionBox.actionBoxData != null)
    const hasPopupOpening = hasActionBoxOpening || isShowTradePanel

    const isModeWithSpecialBuildPhaseButtonRepositioned = useMemo(() => [
        ModeSettingEnum.Seafarers56P,
        ModeSettingEnum.CitiesAndKnights56P,
        ModeSettingEnum.CitiesAndKnightsSeafarers56P,
    ].includes(gameSettings.modeSetting), [gameSettings.modeSetting])

    useEffect(() => {
        if(hasPopupOpening) {
            closeAllExtraButtons()
        }
    }, [hasPopupOpening])

    if(isMyColorSpectator) {
        return <div className={clsx(className, styles.containerLandscape, styles.spectator)}>
            <UIGameCurrentStatusContainer className={styles.currentStatus}/>
            <UIGameTimerContainer
                className={styles.timer}
            />
        </div>
    }

    if(isReplay) {
        return <div className={clsx(className, styles.containerLandscape, styles.replay)}>
            <UIGameCurrentStatusContainer className={styles.currentStatus}/>
        </div>
    }

    const offsetForTradeUI = hasBothShipAndCityImprovementButtons ? '-9.5em' : '-0.5em'
    return <div className={clsx(className, styles.containerLandscape, {
        [styles.hideButtonsForTradeUI]: hasPopupOpening,
    })}
    style={{
        marginLeft: offsetForTradeUI,
        gridTemplateColumns: hasBothShipAndCityImprovementButtons
            ? 'repeat(8, 4.5em)'
            : '0 0 repeat(6, 4.5em)'}}>
        {hasCityImprovementButtons && <>
            <div className={styles.knightActionsButton}>
                <UIGameActionButtonKnightActions
                    className={styles.actionButton}
                />
            </div>
            <div className={styles.cityImprovementsButton}>
                <UIGameActionButtonCityImprovements
                    className={styles.actionButton}
                />
            </div>
        </>}
        {hasShipButtons && <>
            <div className={styles.shipButton} style={{
                gridColumn: hasBothShipAndCityImprovementButtons ? '1' : '3',
            }}>
                <UIGameActionButtonShip
                    className={styles.actionButton}
                />
            </div>
            <div className={styles.shipMoveButton} style={{
                gridColumn: hasBothShipAndCityImprovementButtons ? '2' : '4',
            }}>
                <UIGameActionButtonShipMove
                    className={styles.actionButton}
                />
            </div>
        </>}

        {hasSpecialBuildPhaseButton && !isModeWithSpecialBuildPhaseButtonRepositioned &&
            <div className={styles.specialBuildPhaseButtonContainer}>
                <UIGameActionButtonSpecialBuildPhase
                    className={styles.specialBuildPhaseButton}
                />
            </div>
        }

        <div className={styles.currentStatusContainer}>
            {hasSpecialBuildPhaseButton && isModeWithSpecialBuildPhaseButtonRepositioned &&
                <UIGameActionButtonSpecialBuildPhase
                    className={styles.specialBuildPhaseButton}
                />
            }
            <UIGameCurrentStatusContainer className={styles.currentStatus}/>
        </div>

        <UIGameTimerContainer
            className={styles.timer}
        />
        <div className={styles.tradeButton}>
            <UIGameActionButtonTrade
                className={styles.actionButton}
            />
        </div>
        {hasBuyDevelopmentCardButton &&
            <div className={styles.buyDevelopmentCardButton}>
                <UIGameActionButtonBuyDevelopmentCard
                    className={styles.actionButton}
                />
            </div>
        }
        {hasCityWallButton &&
            <div className={styles.cityWallButton}>
                <UIGameActionButtonCityWall
                    className={styles.actionButton}
                />
            </div>
        }
        <div className={styles.roadButton}>
            <UIGameActionButtonRoad
                className={styles.actionButton}
            />
        </div>
        <div className={styles.settlementButton}>
            <UIGameActionButtonSettlement
                className={styles.actionButton}
            />
        </div>
        <div className={styles.cityButton}>
            <UIGameActionButtonCity
                className={styles.actionButton}
            />
        </div>
        <div className={styles.turnButton}>
            <UIGameActionButtonTurn
                className={styles.actionButton}
            />
        </div>
    </div>
}

const UIGameActionButtonContainerPotrait = ({
    className,
}: UIGameActionButtonContainerProps) => {
    const currentStateValidator = useCurrentStateValidator()
    const hasCityImprovementButtons = useGameStore((state) => state.gameState.mechanicCityImprovementsState != null)
    const hasBuyDevelopmentCardButton = useGameStore((state) => state.gameState.mechanicDevelopmentCardsState != null)
    const isShowTradePanel = useGameStore((state) => state.gameTrade.isShowTradePanel)
    const hasSpecialBuildPhaseButton = useGameStore((state) => state.gameState.mechanicSpecialBuildPhaseState != null)
    const myColor = useGameStore((state) => state.myColor)
    const isMyTurn = currentStateValidator.currentTurnPlayers.includes(myColor)
    const isReplay = useIsReplay()
    const closeAllExtraButtons = useGameStore((state) => state.gameActionButtons.closeAllExtraButtons)

    useEffect(() => {
        if(!isMyTurn) {
            closeAllExtraButtons()
        }
    }, [isMyTurn])

    if(isShowTradePanel || isReplay) {
        return null
    }

    if(!isMyTurn) {
        if(hasSpecialBuildPhaseButton) {
            return <div className={clsx(className, styles.containerPotrait)}>
                <UIGameActionButtonSpecialBuildPhase
                    className={styles.actionButton}
                    hideTooltip
                />
            </div>
        }
        return null
    }

    return <div className={clsx(className, styles.containerPotrait)}>
        <UIGameActionButtonTrade
            className={styles.actionButton}
            hideTooltip
        />
        {hasBuyDevelopmentCardButton &&
            <div className={styles.buyDevelopmentCardButton}>
                <UIGameActionButtonBuyDevelopmentCard
                    className={styles.actionButton}
                    hideTooltip
                />
            </div>
        }
        {hasCityImprovementButtons &&
            <UIGameActionButtonCityImprovements
                className={styles.actionButton}
                hideTooltip
            />
        }
        <UIGameActionButtonTurn
            className={styles.actionButton}
            hideTooltip
        />
    </div>
}

export const UIGameActionButtonContainer = ({
    isPortraitLayout,
    className,
}: UIGameActionButtonContainerProps) => {
    if(isPortraitLayout) {
        return <UIGameActionButtonContainerPotrait className={className} />
    }
    return <UIGameActionButtonContainerLandscape className={className} />
}
